import type { CurrencyHandler } from '@/hooks/useCurrency';
import type {
  EmploymentTypeInstance,
  EmploymentTypeValue,
} from '@/types/employmentType';
import type { TFunction } from 'i18next';

type CandidateSalarySource = {
  employment_types?: EmploymentTypeInstance[];
  compensation?: { values?: EmploymentTypeValue[] };
  compensation_currency?: string;
  /**
   * Flat monthly salary when employment_types/compensation are absent.
   * Retained for legacy client compatibility — used as last-resort listing fallback.
   */
  salary?: number;
};

function isSalaryAmountFieldId(fieldId: string): boolean {
  return (
    (fieldId.includes('salary') || fieldId.includes('rate')) &&
    !fieldId.includes('_max') &&
    !fieldId.includes('interval')
  );
}

function getSalaryFromEmploymentInstance(
  instance: { values?: EmploymentTypeValue[] } | undefined,
  curr: (value: number) => string,
  t: TFunction,
  options?: { requireInterval?: boolean },
): string | null {
  if (!instance?.values?.length) {
    return null;
  }

  const salaryField = instance.values.find((v) =>
    isSalaryAmountFieldId(v.field.id),
  );
  const intervalField = instance.values.find((v) =>
    v.field.id.includes('interval'),
  );

  if (!salaryField) {
    return null;
  }

  if (options?.requireInterval && !intervalField) {
    return null;
  }

  const value = salaryField.numeric_value ?? 0;
  const interval = intervalField?.text_value || 'monthly';
  if (value <= 0 && options?.requireInterval) {
    return null;
  }

  return `${curr(value)} ${t(interval)}`;
}

function legacySalaryLabel(
  candidate: CandidateSalarySource,
  curr: (value: number) => string,
  t: TFunction,
): string | null {
  if (candidate.salary != null && candidate.salary > 0) {
    return `${curr(candidate.salary)} ${t('PER_MONTH')}`;
  }
  return null;
}

/** Listing rows: employment_types or optional compensation snapshot, with legacy salary fallback. */
export function formatListingSalary(
  candidate: CandidateSalarySource,
  defaultCurrency: CurrencyHandler,
  t: TFunction,
  options?: { useCompensation?: boolean },
): string | null {
  const { curr } = defaultCurrency.changeCurrency(
    candidate.compensation_currency ?? '',
  );

  if (options?.useCompensation && candidate.compensation) {
    const fromCompensation = getSalaryFromEmploymentInstance(
      candidate.compensation,
      curr,
      t,
    );
    if (fromCompensation) {
      return fromCompensation;
    }
    return legacySalaryLabel(candidate, curr, t);
  }

  const employmentType = candidate.employment_types?.[0];
  const fromEmployment = getSalaryFromEmploymentInstance(
    employmentType,
    curr,
    t,
  );
  if (fromEmployment) {
    return fromEmployment;
  }

  return legacySalaryLabel(candidate, curr, t);
}

/** Profile / appoint views: compensation or employment_types only — no legacy salary field. */
export function formatProfileSalary(
  candidate: CandidateSalarySource,
  curr: (value: number) => string,
  t: TFunction,
): string | null {
  const fromCompensation = getSalaryFromEmploymentInstance(
    candidate.compensation,
    curr,
    t,
    { requireInterval: true },
  );
  if (fromCompensation) {
    return fromCompensation;
  }

  return getSalaryFromEmploymentInstance(
    candidate.employment_types?.[0],
    curr,
    t,
    { requireInterval: true },
  );
}

/**
 * Job listing/dashboard salary from employment_type values (min/max + optional interval).
 * Returns null when no positive salary/rate values are present.
 */
export function formatEmploymentSalaryRange(
  values: EmploymentTypeValue[] | undefined,
  curr: (value: number) => string,
  t: TFunction,
): string | null {
  if (!values?.length) {
    return null;
  }

  const salaryField = values.find((value) =>
    isSalaryAmountFieldId(value.field.id),
  );
  const maxField = values.find(
    (value) =>
      value.field.id.includes('salary_max') ||
      value.field.id.includes('rate_max'),
  );
  const intervalField = values.find((value) =>
    value.field.id.includes('interval'),
  );
  const min = salaryField?.numeric_value;
  const max = maxField?.numeric_value;
  const interval = intervalField?.text_value;
  const intervalSuffix = interval ? ` ${t(interval)}` : '';

  if (min && max) {
    return `${curr(min)} - ${curr(max)}${intervalSuffix}`;
  }
  if (max) {
    return `${curr(max)}${intervalSuffix}`;
  }
  if (min) {
    return `${curr(min)}${intervalSuffix}`;
  }
  return null;
}
